export default (loadEntries = null) => ({
    entries: [],
    loading: false,
    loadError: false,
    _loadGeneration: 0,
    _inFlight: null,
    _destroyed: false,
    open: false,
    dropdownTop: 0,
    dropdownRight: 0,
    _panelTop: 0,
    _panelRight: 0,
    dropdownMaxHeight: 0,
    // Below this viewport width the fixed dropdown spans the screen (full-width sheet
    // near the trigger) instead of hugging the button on the right.
    isMobile: false,
    _mobileMargin: '0.5rem',
    _boundUpdate: null,
    _boundClickOutside: null,
    _boundPageHide: null,
    _morphedCleanup: null,
    _navigateRef: null,
    _actionsCell: null,
    _rowRefreshEl: null,
    _boundRowRefresh: null,
    // Tracks whether the global scroll/resize/click listeners are currently
    // registered. Prevents double-registration if toggle()/open flip into
    // an inconsistent state, and makes cleanup idempotent.
    _listenersActive: false,

    init() {
        // Renderless soft refresh morphs rows directly through Alpine, outside
        // Livewire's morphed hook. Invalidate only this row before patch/removal.
        if (loadEntries) {
            this._rowRefreshEl = this.$root?.closest?.('[data-row-id]') ?? null;
            this._boundRowRefresh = () => this._forceClose();
            this._rowRefreshEl?.addEventListener('resourcerer:row-refreshing', this._boundRowRefresh);
        }
        this._boundUpdate = this.updateDropdownPosition.bind(this);
        this._boundClickOutside = this.handleClickOutside.bind(this);
        // bfcache / regular unload: close and drop global listeners so a
        // restored page does not come back with zombie handlers attached.
        this._boundPageHide = () => {
            this._forceClose();
        };
        window.addEventListener('pagehide', this._boundPageHide);

        // Livewire navigate (back/forward + bfcache): close the dropdown and
        // drop global listeners before the new page initialises. The panel is
        // teleported to <body>; purge it if it is about to survive as a stale
        // clone attached to a dead Alpine scope.
        this._navigateRef = () => {
            this._forceClose();
            const panel = this.$refs?.panel;
            if (panel && panel.isConnected && panel.parentNode === document.body) {
                try { panel.parentNode.removeChild(panel); } catch (e) {}
            }
        };
        window.addEventListener('livewire:navigate', this._navigateRef);
        window.addEventListener('livewire:navigating', this._navigateRef);

        // Livewire morph hook: if our root is gone or got morphed while the
        // dropdown is open, close it and tear down the global listeners so
        // we don't leak scroll/resize/click handlers bound to a detached DOM.
        if (typeof window !== 'undefined' && window.Livewire?.hook) {
            this._morphedCleanup = window.Livewire.hook('morphed', (component) => {
                if (!this.open) return;

                const root = this.$root;
                if (!root || !root.isConnected) {
                    this._forceClose();
                    return;
                }
                // Close only when the morph actually affects our own root –
                // either the root itself was morphed, or one of its ancestors
                // (which can detach/replace our DOM subtree). Morphs *inside*
                // our root (children being updated) are intentionally ignored:
                // they are normal Livewire updates and stale-ref guards in
                // updateDropdownPosition()/handleClickOutside already catch
                // the edge cases where our own refs got invalidated.
                const el = component?.el;
                if (el && (el === root || el.contains(root))) {
                    this._forceClose();
                }
            });
        }
    },
    toggle() {
        if (this._destroyed) return;
        if (this.open) {
            return this.close();
        }

        // Guard: if the button ref is gone / detached (e.g. after a morph
        // that left the component in an odd state), don't attempt to open.
        const button = this.$refs.button;
        if (!button || !button.isConnected) {
            return;
        }

        button.focus();

        // Closed presentation survives x-show's leave transition. Reset before
        // showing again, including while an invalidated transport is settling.
        if (loadEntries) {
            this.entries = [];
            this.loading = true;
            this.loadError = false;
        }
        this.open = true;
        // Text items are not focusable: mousedown blurs the trigger before click.
        // Keep the owning sticky cell above following rows for the whole open lifetime.
        // Retain the cell so cleanup also works after refs/root are detached by a morph.
        this._actionsCell = button.closest?.('td.actions') ?? null;
        this._actionsCell?.classList.add('resource-table-actions-open');
        this.updateDropdownPosition();

        this._registerEventListeners();
        if (loadEntries) this.load();
    },
    async load() {
        if (!loadEntries || !this.open || this._destroyed) return;
        // A close/reopen invalidates the old response, but does not start another
        // request until the old transport settles. Its result is never reused.
        if (this._inFlight) {
            this.loading = true;
            return;
        }
        // Retry hides its focused button; keep keyboard focus on the trigger so
        // Escape still works while loading and after an empty response.
        if (this.$refs.panel?.contains(document.activeElement)) this.$refs.button?.focus();
        const generation = this._loadGeneration;
        this.loading = true;
        this.loadError = false;
        this.entries = [];
        this.updateDropdownPosition();
        const request = Promise.resolve().then(() => loadEntries());
        this._inFlight = request;
        try {
            const entries = await request;
            if (this.open && !this._destroyed && generation === this._loadGeneration) {
                this.entries = entries;
            }
        } catch (error) {
            if (this.open && !this._destroyed && generation === this._loadGeneration) {
                this.loadError = true;
            }
        } finally {
            this._inFlight = null;
            if (generation === this._loadGeneration) {
                this.loading = false;
                this.updateDropdownPosition();
            } else if (this.open && !this._destroyed) {
                this.load();
            }
        }
    },
    canExecuteEntry() {
        return this.open && !this.loading && !this.loadError && !this._destroyed;
    },
    close(focusAfter) {
        const wasOpen = this.open;
        this._forceClose();

        wasOpen && focusAfter && focusAfter.focus();
    },
    updateDropdownPosition() {
        if (!this.open) return;

        // Guard against stale refs. After a Livewire morph the button may
        // have been detached; calling getBoundingClientRect() on a missing
        // node would throw. In that case close the dropdown and drop the
        // global listeners instead of leaking them.
        const button = this.$refs.button;
        if (!button || !button.isConnected) {
            this._forceClose();
            return;
        }

        // Custom callers bind these raw coordinates with their own margins or
        // compare the trigger's bottom during scroll. Keep the legacy values
        // synchronous; only panelStyle uses the deferred viewport-aware layout.
        const triggerRect = button.getBoundingClientRect();
        this.dropdownTop = triggerRect.bottom;
        const panelWidth = this.$refs.panel?.offsetWidth ?? 0;
        this.dropdownRight = Math.min(
            Math.max(8, window.innerWidth - triggerRect.right),
            Math.max(8, window.innerWidth - panelWidth - 8),
        );

        this.isMobile = window.innerWidth < 768;
        this._mobileMargin = window.innerWidth >= 640 ? '1.5rem' : '0.5rem';

        const position = () => {
            if (!this.open) return;
            if (!button.isConnected || this.$refs.button !== button) {
                this._forceClose();
                return;
            }
            const inputRect = button.getBoundingClientRect();
            const panel = this.$refs.panel;
            // scrollHeight includes the full content and padding even when capped;
            // add the borders for the intrinsic box. These layout dimensions ignore
            // the opening scale. Never uncap the live panel to measure it: doing so
            // resets native scrollTop and can feed captured scroll events back here.
            const panelHeight = panel
                ? (panel.scrollHeight ?? 0) + (panel.offsetHeight ?? 0) - (panel.clientHeight ?? 0)
                : 0;
            const panelWidth = panel?.offsetWidth ?? 0;

            const edge = 8;
            const viewportBottom = Math.max(edge, window.innerHeight - edge);
            const belowTop = Math.min(viewportBottom, Math.max(edge, inputRect.bottom + edge));
            const aboveBottom = Math.max(edge, Math.min(viewportBottom, inputRect.top - edge));
            const below = viewportBottom - belowTop;
            const above = aboveBottom - edge;
            const useAbove = panelHeight > below && above > below;
            this.dropdownMaxHeight = useAbove ? above : below;
            this._panelTop = useAbove
                ? aboveBottom - Math.min(panelHeight, above)
                : belowTop;
            this._panelRight = Math.min(
                Math.max(edge, window.innerWidth - inputRect.right),
                Math.max(edge, window.innerWidth - panelWidth - edge),
            );
            this.dropdownRight = this._panelRight;
        };
        // Alpine flushes bindings on nextTick, but x-show also defers status
        // visibility to a frame. Measure after that work at the new mobile width.
        if (this.$nextTick) this.$nextTick(() => requestAnimationFrame(position));
        else position();
    },
    // Style for the fixed panel: full-width near the trigger on mobile,
    // right-aligned to the trigger on desktop.
    get panelStyle() {
        if (this.isMobile) {
            // Match the content container's horizontal padding (px-2 < sm, sm:px-6) so the
            // full-width dropdown lines up with the cards instead of the viewport edge.
            const margin = this._mobileMargin;

            return { top: `${this._panelTop}px`, maxHeight: `${this.dropdownMaxHeight}px`, left: margin, right: margin };
        }

        return { top: `${this._panelTop}px`, maxHeight: `${this.dropdownMaxHeight}px`, right: `${this._panelRight}px` };
    },
    handleClickOutside(event) {
        // Check if the click was inside the container or the teleported dropdown
        const clickedInsideContainer = this.$refs.container?.contains(event.target);
        const clickedInsideDropdown = this.$refs.panel?.contains(event.target);

        if (!clickedInsideContainer && !clickedInsideDropdown) {
            this.close();
        }
    },
    _registerEventListeners() {
        if (this._listenersActive) return;
        window.addEventListener('scroll', this._boundUpdate, true);
        window.addEventListener('resize', this._boundUpdate);
        document.addEventListener('click', this._boundClickOutside);
        this._listenersActive = true;
    },
    _cleanupEventListeners() {
        if (!this._listenersActive) return;
        window.removeEventListener('scroll', this._boundUpdate, true);
        window.removeEventListener('resize', this._boundUpdate);
        document.removeEventListener('click', this._boundClickOutside);
        this._listenersActive = false;
    },
    // Close unconditionally and drop listeners. Used by morph / pagehide /
    // stale-ref paths where we cannot rely on this.open being accurate.
    _forceClose() {
        this.open = false;
        this._loadGeneration++;
        // Keep presentation intact while the panel leaves. open already gates
        // execution; reopening fetches fresh permissions instead of reusing it.
        this._actionsCell?.classList.remove('resource-table-actions-open');
        this._actionsCell = null;
        this._cleanupEventListeners();
    },
    destroy() {
        this._rowRefreshEl?.removeEventListener('resourcerer:row-refreshing', this._boundRowRefresh);
        this._rowRefreshEl = null;
        this._boundRowRefresh = null;
        this._destroyed = true;
        this._forceClose();
        this.entries = [];
        this.loading = false;
        this.loadError = false;
        this._inFlight = null;
        if (this._boundPageHide) {
            window.removeEventListener('pagehide', this._boundPageHide);
            this._boundPageHide = null;
        }
        if (typeof this._morphedCleanup === 'function') {
            this._morphedCleanup();
            this._morphedCleanup = null;
        }
        if (this._navigateRef) {
            window.removeEventListener('livewire:navigate', this._navigateRef);
            window.removeEventListener('livewire:navigating', this._navigateRef);
            this._navigateRef = null;
        }
    },
});
